import type { SizeButtonStyle } from '../constants';
import type { Size, StockLevelFieldsFragment } from '../graphql/codegen';

import { IS_DEV_WIREMOCK } from '../constants';
import { StockLevelMessageKey } from '../graphql/codegen';

export const clearAndUpper = (text: string) => text.replace(/-|_/, '').toUpperCase();

export const toPascalCase = (text: string) => text.replace(/(^\w|-\w)|(^\w|_\w)/g, clearAndUpper);

export const capitalizeFirst = (text: string) => `${text.charAt(0).toUpperCase()}${text.slice(1)}`;

/**
 * Converts a given string to kebab case.
 * toKebabCase("Hello World"); // Output: "hello-world"
 * toKebabCase("helloWorld"); // Output: "hello-world"
 *
 * @param text The input string to be converted.
 * @returns The input string converted to kebab case.
 */
export const toKebabCase = (text: string) =>
  text
    .replace(/\W+/g, ' ')
    .split(/ |\B(?=[A-Z])/)
    .map(word => word.toLowerCase())
    .join('-');

export function debounce<T extends unknown[], U>(
  callback: (...args: T) => PromiseLike<U> | U,
  wait: number
) {
  let timeoutId: ReturnType<typeof setTimeout>;

  function debounced(...args: T): Promise<U> {
    clearTimeout(timeoutId);

    return new Promise(resolve => {
      timeoutId = setTimeout(() => resolve(callback(...args)), wait);
    });
  }

  function cancel() {
    clearTimeout(timeoutId);
  }

  debounced.cancel = cancel;

  return debounced;
}

export function throttle<T extends unknown[], U>(
  callback: (...args: T) => PromiseLike<U> | U,
  wait: number
): ((...args: T) => Promise<U>) & { cancel: () => void } {
  let timeoutId: ReturnType<typeof setTimeout> | null = null;
  let lastArgs: T | null = null;
  let lastThis: unknown = null;
  let lastResult: Promise<U> | null = null;

  function throttled(this: unknown, ...args: T): Promise<U> {
    lastArgs = args;
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    lastThis = this;

    if (timeoutId) {
      return lastResult || (Promise.resolve() as Promise<U>);
    }

    lastResult = new Promise<U>(resolve => {
      timeoutId = setTimeout(() => {
        timeoutId = null;

        if (lastArgs !== null) {
          resolve(callback.apply(lastThis, lastArgs));
          lastArgs = null;
          lastThis = null;
        } else {
          resolve(undefined as U);
        }
      }, wait);
    });

    return lastResult;
  }

  function cancel() {
    if (timeoutId !== null) {
      clearTimeout(timeoutId);
      timeoutId = null;
    }

    lastArgs = null;
    lastThis = null;
    lastResult = null;
  }

  throttled.cancel = cancel;

  return throttled;
}

export const toBase64 = (str: string) =>
  typeof window === 'undefined' ? Buffer.from(str).toString('base64') : window.btoa(str);

/**
 * DD/MM/YYYY HH:mm:SS or YYYY/MM/DD HH:mm:SS
 */
export const convertCESTStringToDate = (str?: string): Date | null => {
  const regex = /^(\d{2}|\d{4})\/(\d{2})\/(\d{4}|\d{2}) (\d{2}):(\d{2}):(\d{2})$/g;

  if (str && regex.test(str)) {
    const dateTimePieces = str.split(' ');
    const datePieces = dateTimePieces[0].split('/');
    const timePieces = dateTimePieces[1].split(':');

    if (datePieces[0].length === 4) {
      datePieces.reverse();
    }

    const result = new Date(
      `${datePieces[2]}-${datePieces[1]}-${datePieces[0]}T${timePieces[0]}:${timePieces[1]}:${timePieces[2]}+0200`
    );

    return result;
  }

  return null;
};

export const buildApiUrl = (path: string, locale?: string): string => {
  const urlWithLocale = locale ? locale.replace('-', '_') : '';
  const prefix = IS_DEV_WIREMOCK ? process.env.RUNTIME_ORIGIN : '';

  return `${prefix}/${urlWithLocale.toLowerCase()}${path}`;
};

export type ParsedSize = {
  [k: string]: {
    formattedValue?: string | null;
    purchasable?: boolean;
    willBeAvailable?: boolean;
  };
};

export const parseDoubleSizes = (sizes: (Size | undefined | null)[]) => {
  type ParsedDoubleSize = {
    waist: ParsedSize;
    length: ParsedSize;
  };

  const doubleSizes = sizes.reduce(
    (prev, current) => {
      const parsed: ParsedDoubleSize = prev;

      if (current?.gridValue1 && current.gridValue2) {
        const waist = parsed.waist[current.gridValue1];
        const updatedWaist =
          !waist ||
          (waist.purchasable === false && current.purchasable) ||
          (current.willBeAvailable && !waist.purchasable) ||
          (current.willBeAvailable && !waist.willBeAvailable);

        if (updatedWaist) {
          parsed.waist[current.gridValue1] = {
            formattedValue: current.formattedValue,
            purchasable: waist?.purchasable || !!current.purchasable,
            willBeAvailable: waist?.willBeAvailable || !!current.willBeAvailable,
          };
        }

        const length = parsed.length[current.gridValue2];
        const updatedLenght =
          !length ||
          (length.purchasable === false && current.purchasable) ||
          (current.willBeAvailable && !length.purchasable) ||
          (current.willBeAvailable && !length.willBeAvailable);

        if (updatedLenght) {
          parsed.length[current.gridValue2] = {
            formattedValue: current.formattedValue,
            purchasable: length?.purchasable || !!current.purchasable,
            willBeAvailable: length?.willBeAvailable || !!current.willBeAvailable,
          };
        }
      }

      return parsed;
    },
    { waist: {}, length: {} } as ParsedDoubleSize
  );
  const waistSizes = Object.fromEntries(Object.entries(doubleSizes.waist).sort());
  const lengthSizes = Object.fromEntries(Object.entries(doubleSizes.length).sort());

  return { waistSizes, lengthSizes };
};

export const parseSelectedDoubleSize = (
  selectedSize: string,
  sizes: (Size | undefined | null)[],
  isWaist: boolean
) => {
  const necessarySizes = sizes.filter(size =>
    isWaist ? size?.gridValue1 === selectedSize : size?.gridValue2 === selectedSize
  );
  const { waistSizes, lengthSizes } = parseDoubleSizes(necessarySizes);

  return isWaist ? lengthSizes : waistSizes;
};

export const checkSizeFormatted = (
  sizes: (Size | undefined | null)[],
  country: string
): {
  isSizeFormatted: boolean;
  sizeButtonStyle: SizeButtonStyle;
} => {
  let isSizeFormatted = false;
  let sizeButtonStyle: SizeButtonStyle = 'normal';
  const buttonTypeJapan = /\d\d\s\(JP\s\d{1,2}.{1,4}\)/;
  const buttonType4A = /\d{1,2}\s*\([A-Z]\)/;
  const buttonTypeSizeRange = /^\d{1,}-\d{1,}$/;

  for (let i = 0; i < sizes.length; i++) {
    const sizeValue = sizes[i]?.formattedValue?.toLowerCase();

    if (sizeValue?.includes('(') && sizeValue?.includes(country)) {
      isSizeFormatted = true;
      sizeButtonStyle = sizeValue.toUpperCase().match(buttonTypeJapan) ? 'large' : 'medium';
      break;
    }

    if (sizeValue?.toUpperCase().match(buttonType4A)) {
      isSizeFormatted = true;
      sizeButtonStyle = 'fourA';
      break;
    }

    if (sizeValue?.toUpperCase().match(buttonTypeSizeRange)) {
      isSizeFormatted = false;
      sizeButtonStyle = 'sizeRange';
      break;
    }
  }

  return {
    isSizeFormatted,
    sizeButtonStyle,
  };
};

export const parseStockInformation = (
  gridValue1: string,
  gridValue2: string,
  stockInformation?: StockLevelFieldsFragment | null
) => {
  const thresholdsRight = stockInformation?.stockMapping?.find(
    stockMapping => stockMapping?.key === gridValue1
  )?.value?.thresholdsRight;

  if (thresholdsRight) {
    const length = thresholdsRight.find(
      thresholdRight => thresholdRight?.key === gridValue2
    )?.value;
    const stockLevel = length?.stockLevel || null;
    const stockQuantity = length?.stockQuantity || 0;
    const stockMessage = stockInformation?.stockLevelMessages?.find(
      message => message?.key === stockLevel
    )?.value;

    return {
      key: stockLevel,
      value: stockMessage?.replace('%s', stockQuantity.toString()),
    };
  }

  return (
    stockInformation?.stockLevelMessages?.find(
      stockLevelMessage => stockLevelMessage?.key === StockLevelMessageKey.None
    ) || { key: null, value: '' }
  );
};

export const toHtmlLang = (locale: string) =>
  locale.replace(/(_)([a-z]{2})/, (_i, _j, match) => `-${match.toUpperCase()}`);

export const isDaylightSavingTime = (): boolean => {
  const ref = new Date();
  const jan = new Date(ref.getFullYear(), 0, 1);
  const jul = new Date(ref.getFullYear(), 6, 1);
  const offset = Math.max(jan.getTimezoneOffset(), jul.getTimezoneOffset());

  return ref.getTimezoneOffset() < offset;
};

export const checkIsOpenNow = (schedule: Array<number[] | undefined>): boolean => {
  const now = Date.now();
  const localTime = new Date();
  const currentDay = localTime.getDay();
  const openingHours = schedule[currentDay];

  if (!openingHours) {
    return false;
  }

  const fromTime = isDaylightSavingTime() ? openingHours[0] - 1 : openingHours[0];
  const toTime = isDaylightSavingTime() ? openingHours[1] - 1 : openingHours[1];
  const toMinutes = (+toTime.toFixed(2) - Math.floor(toTime)) * 60;
  const referenceUTCFrom = Date.UTC(
    localTime.getFullYear(),
    localTime.getMonth(),
    localTime.getDate(),
    fromTime,
    0,
    0,
    0
  );
  const referenceUTCTo = Date.UTC(
    localTime.getFullYear(),
    localTime.getMonth(),
    localTime.getDate(),
    toTime,
    toMinutes,
    0,
    0
  );

  return referenceUTCFrom < now && referenceUTCTo > now;
};
export const weekDayStr = new Date().toLocaleDateString(undefined, { weekday: 'long' });

export const xmlToJson = (xml: Node | null) => {
  let obj:
    | {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        [key: string]: any;
      }
    | string = {};

  if (!xml) {
    return obj;
  }

  if (xml.nodeType === 3) {
    obj = xml.nodeValue ?? '';
  }

  if (xml.hasChildNodes() && typeof obj === 'object') {
    for (let i = 0; i < xml.childNodes.length; i++) {
      const item = xml.childNodes.item(i);
      const { nodeName } = item;

      if (typeof obj[nodeName] === 'undefined') {
        obj[nodeName] = xmlToJson(item);
      } else {
        if (typeof obj[nodeName].push === 'undefined') {
          const old = obj[nodeName];

          obj[nodeName] = [];
          obj[nodeName].push(old);
        }

        obj[nodeName].push(xmlToJson(item));
      }
    }
  }

  return obj;
};

export const removeUselessSpaces = (className: string) => {
  const parsedClassName = className.trim().replace(/  +/g, ' ');

  return parsedClassName === ' ' ? '' : parsedClassName;
};

export const removeTagsFromHTML = (html: string | undefined) =>
  html?.replace(/<[^>]+>/g, '')?.replace(/\s{2,}/g, ' ');

export const truncate = (str: string | undefined, limit: number) => {
  if (typeof str === 'undefined') {
    return undefined;
  }

  if (!str?.length) {
    return '';
  }

  if (str.length > limit) {
    return str.substring(0, limit).trim().concat('...');
  }

  return str;
};

export function parseLocale(locale: string): {
  language: string;
  country: string;
  languageIso: string;
} {
  const strArr = locale.split('_');
  const language = strArr[0];
  const country = strArr[1] || '';
  const languageIso = `${language}_${country.toUpperCase()}`;

  return {
    language,
    country,
    languageIso,
  };
}

export const amplienceLocaleWithFallback = (locale: string) => {
  const { country, language } = parseLocale(locale);

  let amplienceLocale = `${language}-${country.toUpperCase()},${language}`;

  if (language !== 'en') {
    amplienceLocale += ',en';
  }

  return amplienceLocale;
};

export const capitalize = (string: string) =>
  string.replace(/(^\w{1})|(\s+\w{1})/g, letter => letter.toUpperCase());
